import React, { useState, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { ModalWithStyles } from './ModalWithStyles';
import { DataTable } from '../../modules/Manage/pages/queues-table/DataTable';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { useMutation } from 'react-query';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { user } from '../../../recoil/atoms/user';
import { updateBusinessID } from '../../../common/changeBusinessID';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { FormikSelect } from '../../../common/formik-material-ui';

const useStyles = makeStyles((theme) => ({
  dropdown: {
    marginLeft: '1vw',
  },
  button: {
    marginLeft: '1vw',
    marginTop: 8,
  },
  autoField: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '190px',
  },
}));

const initialValues = {
  type: 'businessid',
  value: '',
  autoSwitch: true,
};

const validationSchema = Yup.object().shape({
  value: Yup.string().required('Please Enter the Value to Search'),
});

export function SwitchBusinessModal({
  open,
  handleSwitchBusinessModal,
  handleSwitchModal,
}) {
  const classes = useStyles();
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [userAtom, setUserAtom] = useRecoilState(user);
  const [rows, handleRows] = useState([]);
  const [loading, handleLoading] = useState(false);
  const [allFields, handleAllFields] = useState(true);

  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const [
    _,
    onboardingWalkthroughLoadable,
    refreshToggleOnboardingWalkthrough,
  ] = makeApiRequestCached(
    {
      method: 'POST',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_OW,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  React.useEffect(() => {
    if (onboardingWalkthroughLoadable?.state === 'hasValue') {
      const updatedOW =
        onboardingWalkthroughLoadable?.contents?.response?.data?.data;
      updatedOW?.displayOnboarding !==
        userAtom?.onboardingWalkthrough?.displayOnboarding &&
        setUserAtom({
          ...userAtom,
          onboardingWalkthrough: updatedOW,
        });
    }
  }, [onboardingWalkthroughLoadable, userAtom]);

  const get10DlcBrandStatusMessaging = async (bid, apikey) => {
    try {
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.GET_10DLC_STATUS_MESSAGE,
        payload: {
          apikey: apikey,
          call: methodNameEnum.GET_10DLC_STATUS_MESSAGE,
          businessid: bid,
        },
      });
      if (response?.data?.success && response?.data?.result?.[0]) {
        const updatedBrandStatus = {
          brandStatus: response?.data?.result?.[0]?.status ?? '',
          businessNumbersCount: response?.data?.result?.[0]?.count ?? 0,
        };
        return updatedBrandStatus;
      } else {
        enqueueSnackbarWithStyles({
          message: response?.message,
          alertProps: {
            severity: 'error',
          },
        });
        return {
          brandStatus: '',
          businessNumbersCount: 0,
        };
      }
    } catch (error) {
      return {
        brandStatus: '',
        businessNumbersCount: 0,
      };
    }
  };

  const handleSwitch = useCallback((id, apikey, updatedBrandStatus) => {
    const res = updateBusinessID(id, apikey);
    if (res.success) {
      res.user = { ...res.user, brandStatus10Dlc: updatedBrandStatus };
      setUserAtom(res.user);
      handleSwitchModal();
    } else {
      enqueueSnackbarWithStyles({
        message: res.error,
        alertProps: {
          severity: 'error',
        },
      });
    }
  });

  const onHandleSwitch = async (bid, apikey) => {
    refreshToggleOnboardingWalkthrough({});
    const updatedStatusMessage = await get10DlcBrandStatusMessaging(
      bid,
      apikey
    );
    handleSwitch(bid, apikey, updatedStatusMessage);
  };

  const handleFilter = async (values) => {
    handleLoading(true);
    let originalBusinessObject = localStorage.getItem('originalBusinessObject');
    originalBusinessObject = originalBusinessObject
      ? JSON.parse(originalBusinessObject)
      : undefined;
    let response;
    if (originalBusinessObject?.businessid) {
      response = await mutation.mutateAsync({
        endpoint: endpointEnum.SEARCH_BUSINESS,
        omitBusinessIdAndApikey: true,
        payload: {
          apikey: originalBusinessObject?.apikey,
          apiKey: originalBusinessObject?.apikey,
          businessid: originalBusinessObject?.businessid,
          businessId: originalBusinessObject?.businessid,
          call: methodNameEnum.SEARCH_BUSINESS,
          userid: userAtom.userid,
          type: values.type,
          value: values.value,
        },
      });
    } else {
      response = await mutation.mutateAsync({
        endpoint: endpointEnum.SEARCH_BUSINESS,
        payload: {
          apikey: userAtom.apikey,
          call: methodNameEnum.SEARCH_BUSINESS,
          userid: userAtom.userid,
          type: values.type,
          value: values.value,
        },
      });
    }
    handleLoading(false);
    if (!response?.data?.success) {
      handleRows([]);
    } else {
      if (
        response?.data?.result?.length === 1 &&
        response.data.result[0]['No data exists']
      ) {
        handleRows([]);
        enqueueSnackbarWithStyles({
          message: 'There were no results returned from your query',
          alertProps: {
            severity: 'warning',
          },
        });
        return;
      }
      if (values.type !== 'useremail') {
        handleAllFields(false);
      } else {
        handleAllFields(true);
      }
      if (
        response?.data?.result?.length === 1 &&
        values.autoSwitch &&
        response?.data?.result[0].status === 'active'
      ) {
        onHandleSwitch(
          response?.data?.result[0].businessid,
          response?.data?.result[0].apiKey
        );
      } else {
        handleRows(
          response?.data?.result?.map((b) => ({
            ...b,
            fullname: b?.fname ? `${b?.fname} ${b?.lname}` : '',
            id: uuidv4(),
          }))
        );
      }
    }
  };

  function actionsFormatter(params) {
    return (
      <Button
        style={{ minWidth: '76px' }}
        variant="contained"
        color="primary"
        onClick={() => onHandleSwitch(params.row.businessid, params.row.apiKey)}
      >
        Switch
      </Button>
    );
  }

  let columns = [
    {
      field: 'fullname',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'userstatus',
      headerName: 'User Status',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'status',
      headerName: 'Business Status',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'businessid',
      headerName: 'Business ID',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'name',
      headerName: 'Business Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      renderCell: actionsFormatter,
    },
  ];

  if (!allFields) {
    columns = [
      {
        field: 'status',
        headerName: 'Business Status',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'businessid',
        headerName: 'Business ID',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'name',
        headerName: 'Business Name',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        renderCell: actionsFormatter,
      },
    ];
  }

  const sortModel = [
    {
      field: 'businessid',
      sort: 'desc',
    },
  ];

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleFilter(values);
    },
  });

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return 'is-invalid';
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return 'is-valid';
    }

    return '';
  };

  return (
    <ModalWithStyles
      title="Switch Business"
      aria-labelledby="form-dialog-title"
      show={open}
      onHide={() => handleSwitchBusinessModal(false)}
      isLoading={false}
      isSubmitting={false}
      footerComponent={
        <Button
          onClick={() => handleSwitchBusinessModal(false)}
          color="primary"
        >
          Cancel
        </Button>
      }
    >
      <>
        <form onSubmit={formik.handleSubmit}>
          <Box display="flex" sx={{ marginTop: '14px', alignItems: 'center' }}>
            <Box display="flex" flexDirection="column">
              <TextField
                sx={{ marginTop: '8px' }}
                className={getInputClasses('value')}
                name="value"
                autoFocus
                size="small"
                label="Search"
                variant="outlined"
                {...formik.getFieldProps('value')}
              />
              {formik.touched.value && formik.errors.value && (
                <Typography variant="caption" color="error">
                  {formik.errors.value}
                </Typography>
              )}
            </Box>
            <FormControl
              className={classes.dropdown}
              size="small"
              variant="outlined"
            >
              <InputLabel>Filter Type</InputLabel>
              <Select
                name="type"
                label="Active Status"
                {...formik.getFieldProps('type')}
              >
                <MenuItem value={'businessid'}>Business ID</MenuItem>
                <MenuItem value={'useremail'}>User Email</MenuItem>
                <MenuItem value={'number'}>Phone Number</MenuItem>
                <MenuItem value={'businessname'}>Business Name</MenuItem>
              </Select>
            </FormControl>
            <FormikSelect
              name="autoSwitch"
              className={classes.autoField}
              formik={formik}
              label="Auto-Switch If One"
              size="small"
              variant="outlined"
            >
              <MenuItem value={true}>True</MenuItem>
              <MenuItem value={false}>False</MenuItem>
            </FormikSelect>
            <Box>
              <Button
                className={classes.button}
                color="primary"
                variant="contained"
                type="submit"
                disabled={loading}
              >
                Search
              </Button>
            </Box>
          </Box>
        </form>
        <DataTable
          rows={rows}
          columns={columns}
          sortingOrder={['desc', 'asc']}
          sortModel={sortModel}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </>
    </ModalWithStyles>
  );
}
